import React, { createContext, useContext, useState, useEffect } from 'react';
import { useBookmarks } from './BookmarkContext';

const TenderReviewContext = createContext();

const TENDER_REVIEWS_STORAGE_KEY = 'canadabuys-tender-reviews';

// Tender review statuses
export const TENDER_STATUS = {
  UNREVIEWED: 'unreviewed',
  SAVED: 'saved',
  NOT_APPLICABLE: 'not_applicable'
};

export const useTenderReview = () => {
  const context = useContext(TenderReviewContext);
  if (!context) {
    throw new Error('useTenderReview must be used within a TenderReviewProvider');
  }
  return context;
};

export const TenderReviewProvider = ({ children }) => {
  const [reviews, setReviews] = useState(new Map());
  const [loading, setLoading] = useState(true);

  // Load reviews from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(TENDER_REVIEWS_STORAGE_KEY);
      if (saved) {
        const parsedReviews = JSON.parse(saved);
        setReviews(new Map(Object.entries(parsedReviews)));
      }
    } catch (error) {
      console.error('Error loading tender reviews:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save reviews to localStorage whenever reviews change
  useEffect(() => {
    if (!loading) {
      try {
        const reviewsObj = Object.fromEntries(reviews);
        localStorage.setItem(TENDER_REVIEWS_STORAGE_KEY, JSON.stringify(reviewsObj));
      } catch (error) {
        console.error('Error saving tender reviews:', error);
      }
    }
  }, [reviews, loading]);

  const setTenderStatus = (tender, status) => {
    const tenderId = tender.tender_id || tender.reference_number;
    const reviewData = {
      id: tenderId,
      status,
      reviewedAt: new Date().toISOString(),
      tender: {
        id: tenderId,
        title: tender.title || 'Untitled Tender',
        organization: tender.organization?.contracting_entity || 
                     tender.organization?.name || 
                     'Government of Canada',
        closingDate: tender.closing_date,
        categories: tender.categories || [],
        estimatedValue: tender.estimatedValue || null
      }
    };

    setReviews(prev => new Map(prev.set(tenderId, reviewData)));
  };

  const getTenderStatus = (tenderId) => {
    const review = reviews.get(tenderId);
    return review?.status || TENDER_STATUS.UNREVIEWED;
  };

  const getTenderReview = (tenderId) => {
    return reviews.get(tenderId) || null;
  };

  const removeTenderReview = (tenderId) => {
    setReviews(prev => {
      const newMap = new Map(prev);
      newMap.delete(tenderId);
      return newMap;
    });
  };

  const clearAllReviews = () => {
    setReviews(new Map());
  };

  // Convenience methods for specific actions
  const saveTender = (tender) => {
    setTenderStatus(tender, TENDER_STATUS.SAVED);
    // Note: Bookmark integration happens in the Dashboard component
  };
  const unsaveTender = (tenderId) => {
    removeTenderReview(tenderId);
  };
  const markNotApplicable = (tender) => setTenderStatus(tender, TENDER_STATUS.NOT_APPLICABLE);

  // Check status methods
  const isSaved = (tenderId) => getTenderStatus(tenderId) === TENDER_STATUS.SAVED;
  const isNotApplicable = (tenderId) => getTenderStatus(tenderId) === TENDER_STATUS.NOT_APPLICABLE;
  const isUnreviewed = (tenderId) => getTenderStatus(tenderId) === TENDER_STATUS.UNREVIEWED;

  // Get tenders by status
  const getTendersByStatus = (status) => {
    return Array.from(reviews.values()).filter(review => review.status === status);
  };

  // Review statistics
  const getReviewStats = () => {
    const allReviews = Array.from(reviews.values());
    const today = new Date().toDateString();
    const todayReviews = allReviews.filter(review => 
      new Date(review.reviewedAt).toDateString() === today
    );

    return {
      total: allReviews.length,
      saved: allReviews.filter(r => r.status === TENDER_STATUS.SAVED).length,
      notApplicable: allReviews.filter(r => r.status === TENDER_STATUS.NOT_APPLICABLE).length,
      todayTotal: todayReviews.length,
      todaySaved: todayReviews.filter(r => r.status === TENDER_STATUS.SAVED).length,
      todayNotApplicable: todayReviews.filter(r => r.status === TENDER_STATUS.NOT_APPLICABLE).length
    };
  };

  // Filter tenders based on review status
  const filterTendersByReviewStatus = (tenders, statusFilter) => {
    if (!statusFilter || statusFilter === 'all') return tenders;
    
    return tenders.filter(tender => {
      const tenderId = tender.tender_id || tender.reference_number;
      const status = getTenderStatus(tenderId);
      return status === statusFilter;
    });
  };

  const value = {
    reviews,
    loading,
    
    // Status management
    setTenderStatus,
    getTenderStatus,
    getTenderReview,
    removeTenderReview,
    clearAllReviews,
    
    // Convenience methods
    saveTender,
    unsaveTender,
    markNotApplicable,
    
    // Check methods
    isSaved,
    isNotApplicable,
    isUnreviewed,
    
    // Data retrieval
    getTendersByStatus,
    getReviewStats,
    filterTendersByReviewStatus,
    
    // Constants
    TENDER_STATUS
  };

  return (
    <TenderReviewContext.Provider value={value}>
      {children}
    </TenderReviewContext.Provider>
  );
};